<template lang="pug">
    div(class="_col", :style="style")
        slot
</template>

<script>
    /**
     * A column in the [grid](#/classes/grid) system.
     *
     * Most commonly, this is used as an element rather than a component (which would have higher memory and compute
     * overhead) by using `.ib-col` in your template rather than `ib-col`. However, this must be used as a component to
     * use the `width` prop.
     */
    export default {
        name: 'ib-col',

        props: {
            /**
             * Column width in any CSS units, or `auto` to use all remaining space. This is an
             * alternative to the `.[s-xxxl][1-12]` classes.
             */
            width: String
        },

        computed: {
            style() {
                const width = this.width;
                return width == null
                    ? null
                    : width == 'auto'
                        ? { flex: '1 1 0' }
                        : { flexBasis: width, maxWidth: width };
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    .ib-col,
    ._col {
        min-width: 0;
        max-width: 100%;
        flex: 0 0 100%;

        &.flex {
            display: flex;
            flex-direction: column;
            max-height: 100%;
        }
        &.auto {
            flex: 1 1 0;
        }
        &.grow {
            height: 100%;
        }
        &.shrink {
            max-height: 100%;
        }
        &.grow,
        &.shrink {
            overflow-x: hidden;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            --padding-top: 0;
            --padding-right: 0;
            --padding-bottom: 0;
            --padding-left: 0;
        }
    }

    .gen(@name) {
        .gen-col(@n, @i: 1) when (@i <= @n) {
            @perc: (@i * 100% / @n);
            .@{name}@{i} {
                flex-basis: @perc;
                max-width: @perc;
            }
            .offset-@{name}@{i} {
                margin-left: @perc;
            }
            .gen-col(@n, (@i + 1));
        }
        .gen-col(@grid-cols);
    }

    .gen(s);

    @media screen and (min-width:@break-m) {
        .gen(m);
    }

    @media screen and (min-width:@break-l) {
        .gen(l);
    }

    @media screen and (min-width:@break-xl) {
        .gen(xl);
    }

    @media screen and (min-width:@break-xxl) {
        .gen(xxl);
    }

    @media screen and (min-width:@break-xxxl) {
        .gen(xxxl);
    }


    /** Column classes */

    .ib-col,
    ._col {
        &.spaced {
            background-clip: padding-box;
            border: @grid-spaced solid transparent;
        }
        &.spaced-top {
            background-clip: padding-box;
            border-top: @grid-spaced solid transparent;
        }
        &.spaced-bottom {
            background-clip: padding-box;
            border-bottom: @grid-spaced solid transparent;
        }
        &.spaced-left {
            background-clip: padding-box;
            border-left: @grid-spaced solid transparent;
        }
        &.spaced-right {
            background-clip: padding-box;
            border-right: @grid-spaced solid transparent;
        }
    }
    [dir=ltr],
    [dir=rtl] [dir=ltr] {
        .ib-col,
        ._col {
            &.spaced-start {
                border-left: @grid-spaced solid transparent;
            }
            &.spaced-end {
                border-right: @grid-spaced solid transparent;
            }
        }
    }
    [dir=rtl],
    [dir=ltr] [dir=rtl] {
        .ib-col,
        ._col {
            &.spaced-start {
                border-right: @grid-spaced solid transparent;
            }
            &.spaced-end {
                border-left: @grid-spaced solid transparent;
            }
        }
    }
</style>
